import { LineChartOutlined } from '@ant-design/icons';
import { Col, Divider, Drawer, Row, Space, Tooltip } from 'antd';
import { find, get } from 'lodash';
import React, { useMemo, useState } from 'react';
import { withNamespaces } from 'react-i18next';
import { useSelector } from 'react-redux';

import { Button, ConfirmModal } from '../../../../components';
import MetabaseMonthlyMileageChart from '../../../../components/MetabaseMonthlyMileageChart';
import FullScreenTripModal from '../../../../components/Modal/FullScreenTripModal';
import ReceiptDeleteConfirmationModal from '../../../../components/Modal/ReceiptDeleteConfirmationModal';
import ReceiptStatusHistoryModal from '../../../../components/Modal/ReceiptStatusHistoryModal';
import ReimbursementReceiptConfirmationWithMoreInfoTripModal from '../../../../components/Modal/ReimbursementReceiptConfirmationWithMoreInfoTripModal';
import SubmitReimbursementsForApprovalConfirmation from '../../../../components/Modal/SubmitReimbursementsForApprovalConfirmation';
import TripsCommentsModal from '../../../../components/Modal/TripsCommentsModal';
import TripsSubmissionHistoryModal from '../../../../components/Modal/TripsSubmissionHistoryModal';
import OneReceiptPerPeriodSummary from '../../../../components/Receipts/OneReceiptPerPeriodSummary';
import ReceiptSummaryCard from '../../../../components/Receipts/ReceiptSummaryCard';
import FixedReceiptDetailsTable from '../../../../components/Table/FixedReceiptDetailsTable';
import TripsTableByPeriod from '../../../../components/Table/TripsTable/TripsTableByPeriod';
import StatusTag from '../../../../components/Tag/StatusTag';
import Text from '../../../../components/Text';
import LinkText from '../../../../components/Text/LinkText';
import { INTERNAL_LINKS, RECEIPT_TYPES, STATUS_LIST } from '../../../../enum';
import useTripsComments from '../../../../hooks/useTripsComments';
import { setModalFullscreenWidth } from '../../../../utils/common';
import {
  hasCompanyAdminRole,
  hasCompanyManagerOrAdminRole,
  hasUserRole,
} from '../../../../utils/roles';
import {
  selectStoreCurrentAuthUser,
  selectStoreCurrentCompany,
} from '../../../../utils/storeSelectors';
import { formatPaymentPeriodLabel } from '../../../../utils/trip-receipts';
import ReceiptFooterAdminActionBoxContent from '../../../receipt/details/ReceiptFooterAdminActionBoxContent';
import ReceiptFooterUserActionBoxContent from '../../../receipt/details/ReceiptFooterUserActionBoxContent';
import { canDeleteReceipt, canUpdateReceiptStatus } from '../../../receipt/receipt-permissions';
import { canAddCommentOnTrip } from '../../../trips/trips-permissions';
import ReimbursementDetailsPageContainer from '../../ReimbursementDetailsPageContainer';
import classNames from './styles.module.scss';
import useReceiptDetailsState from './useReceiptDetailsState';

const ReimbursementsDetailsOnPeriodView = props => {
  const { t, history, match, viewType } = props;
  const isReceiptViewType = viewType === 'receipt';
  const receiptId = match.params.id;
  const [searchTerm, setSearchTerm] = useState('');

  const handleSearchTermChange = newSearchTerm => {
    setSearchTerm(newSearchTerm);
  };

  const {
    // Booleans
    isReceiptLoading,
    isApprovingReceipt,
    isRejectingTrip,
    isRejectingReceipt,
    isTripDetailsVisible,
    isStatusHistoryVisible,
    isRequestingMoreInfo,
    isProcessingMoreInfoRequest,
    isResubmittingReceiptsForApproval,
    isApprovalConfirmationVisible,
    isRejectConfirmationVisible,
    isTripRejectConfirmationVisible,
    isApprovalWithMoreInfoVisible,
    isResubmitConfirmationVisible,
    isDeleteReceiptModalVisible,
    isMonthlyChartVisible,
    isSubmissionHistoryVisible,
    // General variables
    receipt,
    receiptTrips,
    tripMapConfig,
    selectedTripsMoreInfo,
    moreInfoTripsCount,
    totalTripsCount,
    paginationConfig,
    // Handlers & Togglers
    handleViewTripMapFullScreen,
    handleTripMapClose,
    handleMoreInfoRequest,
    handleApprove,
    handleDeny,
    handleTripDeny,
    handleResubmitForApproval,
    toggleStatusHistoryModal,
    toggleRequestMoreInfo,
    toggleSubmissionHistoryModal,
    openApprovalConfirmation,
    closeApprovalConfirmation,
    openRejectionConfirmation,
    closeRejectionConfirmation,
    openTripRejectionConfirmation,
    closeTripRejectionConfirmation,
    openResubmitConfirmation,
    closeResubmitConfirmation,
    updateSelectedTripsForMoreInfo,
    setIsMonthlyChartVisible,
    handlePageChange,
    handleTableSort,
    handlePageSizeChange,
    updateTripLatestComment,
    openDeleteReceiptModal,
    closeDeleteReceiptModal,
    deleteReceiptMutation,
    retryMassTransactionMutation,
  } = useReceiptDetailsState({ t, history, receiptId, searchTerm });

  const {
    tripCommentsQuery,
    commentsPaginationConfig,
    tripIdToViewComments,
    isAddingComment,
    isTripCommentsModalVisible,
    openTripCommentsModal,
    closeTripCommentsModal,
    addCommentToTrip,
    handleCommentsPageChange,
  } = useTripsComments({ onAddTripComment: updateTripLatestComment });

  const currentCompany = useSelector(selectStoreCurrentCompany);
  const authUserProfile = useSelector(selectStoreCurrentAuthUser);
  const companySettings = useSelector(store => store.common.currentCompany.companySettingId);
  const { groupList } = useSelector(store => store.group);

  const CAN_REJECT_TRIP =
    hasCompanyManagerOrAdminRole(authUserProfile) &&
    !!companySettings?.settings?.approveReceipt &&
    [
      STATUS_LIST().Status.APPROVED,
      STATUS_LIST().Status.PENDING,
      STATUS_LIST().Status.OVERDUE,
    ].includes(receipt?.status) &&
    !isRequestingMoreInfo;

  const paymentPeriodString = useMemo(() => {
    return formatPaymentPeriodLabel(
      get(receipt, 'period'),
      'paymentPeriodStart',
      'paymentPeriodEnd',
      'MMM DD, YYYY',
      'MMM DD, YYYY',
      receipt?.type === RECEIPT_TYPES.FIXED_TYPE,
    );
  }, [receipt]);

  return (
    <div className={classNames.reimbursementDetailsWrapper}>
      <ReimbursementDetailsPageContainer
        t={t}
        style={{
          paddingBottom:
            canUpdateReceiptStatus(authUserProfile, undefined, true) || isRequestingMoreInfo
              ? 45
              : undefined,
        }}
        loading={!receipt || isReceiptLoading}
        companySettings={companySettings}
        history={history}
        sideActionComponent={
          receipt &&
          hasCompanyAdminRole(authUserProfile) && (
            <Tooltip
              placement="left"
              enabled={!canDeleteReceipt(receipt)}
              title={t('onlyFixedReimbursementsCanBeDeleted')}
            >
              <Button disabled={!canDeleteReceipt(receipt)} onClick={openDeleteReceiptModal}>
                {t('Delete')}
              </Button>
            </Tooltip>
          )
        }
        breadcrumbs={
          receipt
            ? [
                {
                  onClick: () =>
                    isReceiptViewType
                      ? history.push({
                          pathname: INTERNAL_LINKS.RECEIPTS,
                          search: get(history, 'location.state.goReceiptsQueryParams', ''),
                        })
                      : history.push({
                          pathname: INTERNAL_LINKS.REIMBURSEMENT,
                          search: get(history, 'location.state.goReimbursementsQueryParams', ''),
                        }),
                  label: isReceiptViewType
                    ? t('Receipts')
                    : t(
                        companySettings?.settings?.tripApprovalWorkflow
                          ? 'Reimbursements'
                          : 'taxVault',
                      ),
                },
                {
                  label: (
                    <Space align="center" size="small">
                      {isReceiptViewType ? (
                        <div>{receipt.seqId || 'SeqID'}</div>
                      ) : (
                        <div>{paymentPeriodString}</div>
                      )}
                      {receipt?.status && (
                        <StatusTag
                          status={
                            receipt.status === STATUS_LIST().Status.PENDING
                              ? STATUS_LIST().Status.AWAITING_APPROVAL
                              : receipt.status
                          }
                        />
                      )}

                      <Space
                        align="center"
                        size="small"
                        split={
                          <Divider
                            type="vertical"
                            style={{ margin: 0, height: '18px', borderLeft: '1px solid #24272B' }}
                          />
                        }
                      >
                        {!isReceiptLoading &&
                          Array.isArray(receipt.statusHistory) &&
                          !!receipt.statusHistory.length && (
                            <LinkText onClick={toggleStatusHistoryModal}>
                              {t('statusHistory')}
                            </LinkText>
                          )}
                        {receipt?.type === RECEIPT_TYPES.VARIABLE_TYPE && (
                          <LinkText onClick={toggleSubmissionHistoryModal}>
                            {t('tripsSubmissionHistory')}
                          </LinkText>
                        )}
                      </Space>
                    </Space>
                  ),
                },
              ]
            : undefined
        }
      >
        <Row style={{ marginBottom: 60 }}>
          <Col flex={1}>
            {(isReceiptViewType ||
              (!isReceiptViewType && receipt?.type === RECEIPT_TYPES.FIXED_TYPE)) && (
              <ReceiptSummaryCard
                t={t}
                loading={isReceiptLoading}
                receipt={receipt}
                onRetry={() =>
                  retryMassTransactionMutation.mutateAsync(receipt.dwollaCorrelationId._id)
                }
              />
            )}

            {!isReceiptViewType && receipt && receipt.type === RECEIPT_TYPES.VARIABLE_TYPE && (
              <OneReceiptPerPeriodSummary t={t} receipt={receipt} />
            )}
          </Col>
        </Row>

        <Row style={{ marginBottom: 20 }} justify="space-between" gutter={[16, 16]}>
          <Col flex={1}>
            {receipt && receipt.type === RECEIPT_TYPES.FIXED_TYPE && (
              <Text variant="h4">{t('fixedAmountDetails')}</Text>
            )}

            {receipt && receipt.type === RECEIPT_TYPES.VARIABLE_TYPE && (
              <Text variant="h4">{t('tripsDuringThisPeriod')}</Text>
            )}
          </Col>

          {companySettings?.settings?.mileageCap && hasCompanyManagerOrAdminRole(authUserProfile) && (
            <Col>
              <Button
                size="sm"
                type="secondary"
                onClick={() => setIsMonthlyChartVisible(true)}
                icon={<LineChartOutlined style={{ fontSize: '16px' }} />}
              >
                {t('viewHistoricalMileage')}
              </Button>
            </Col>
          )}
        </Row>

        <Row>
          <Col flex={1}>
            {receipt && receipt.type === RECEIPT_TYPES.FIXED_TYPE && (
              <FixedReceiptDetailsTable
                t={t}
                dataSource={[
                  {
                    _id: receipt._id,
                    date: receipt.created,
                    currency: receipt.currency,
                    paymentFor: receipt.period.paymentPeriodEnd,
                    fixedAmount: receipt.totalAmount,
                    favrInformations: receipt.favrInformations,
                  },
                ]}
              />
            )}

            {receipt && receipt.type === RECEIPT_TYPES.VARIABLE_TYPE && (
              <TripsTableByPeriod
                onChange={({ current }, filters, sorters) => {
                  handlePageChange(current);
                  handleTableSort(sorters?.columnKey, sorters?.order);
                }}
                pagination={{
                  pageSize: paginationConfig.pageSize,
                  total: paginationConfig.total,
                  current: paginationConfig.current,
                  onShowSizeChange: handlePageSizeChange,
                }}
                t={t}
                onSearchChange={handleSearchTermChange}
                asyncSort
                showSearchInput
                hideGPSColumns
                authUserID={authUserProfile.profile._id}
                dataSource={receiptTrips || []}
                canRejectTrip={CAN_REJECT_TRIP}
                onTripReject={openTripRejectionConfirmation}
                isRejectingTrip={isRejectingReceipt}
                handleViewFullScreen={handleViewTripMapFullScreen}
                onViewMoreComments={openTripCommentsModal}
                rowSelection={
                  isRequestingMoreInfo
                    ? {
                        type: 'checkbox',
                        selectedRowKeys: selectedTripsMoreInfo,
                        getCheckboxProps: item => ({
                          disabled: item.status === STATUS_LIST().Status.MORE_INFO,
                        }),
                        onChange: selectedRowKeys => {
                          if (hasCompanyManagerOrAdminRole(authUserProfile)) {
                            updateSelectedTripsForMoreInfo(selectedRowKeys);
                          }
                        },
                      }
                    : undefined
                }
              />
            )}
          </Col>
        </Row>
      </ReimbursementDetailsPageContainer>

      <ReceiptStatusHistoryModal
        t={t}
        title={isReceiptViewType ? t('rcStatusHistory') : t('rbStatusHistory')}
        visible={isStatusHistoryVisible}
        statusList={receipt ? receipt.statusHistory : []}
        currency={receipt ? get(receipt, 'currency') : undefined}
        proration={receipt ? receipt.proration : undefined}
        paymentPeriodEnd={receipt ? get(receipt, 'period.paymentPeriodEnd') : undefined}
        onCancel={toggleStatusHistoryModal}
      />

      <FullScreenTripModal
        visible={isTripDetailsVisible}
        mapConfig={tripMapConfig}
        onCancel={handleTripMapClose}
      />

      <ConfirmModal
        loading={isRejectingReceipt}
        visible={isRejectConfirmationVisible}
        title={
          isReceiptViewType
            ? t('sureYouWantToRejectReceipt')
            : t('sureYouWantToRejectReimbursement')
        }
        message={
          moreInfoTripsCount
            ? isReceiptViewType
              ? t('rejectAllTripsAndSomeWithMoreInfoInReceipt')
              : t('rejectAllTripsAndSomeWithMoreInfoInReimbursement')
            : undefined
        }
        onOk={() => handleDeny(receiptId)}
        onCancel={closeRejectionConfirmation}
        okText={t('yes')}
        cancelText={t('no')}
      />

      {receipt && hasCompanyManagerOrAdminRole(authUserProfile) && (
        <ConfirmModal
          loading={isApprovingReceipt}
          visible={isApprovalConfirmationVisible}
          title={t('sureYouWantToApproveTrips', {
            count: moreInfoTripsCount || 0,
          })}
          onOk={() => handleApprove(match.params.id)}
          onCancel={closeApprovalConfirmation}
          okText={t('yes')}
          cancelText={t('no')}
        />
      )}

      {receipt && hasCompanyManagerOrAdminRole(authUserProfile) && (
        <ConfirmModal
          loading={isRejectingTrip}
          visible={isTripRejectConfirmationVisible}
          title={t('sureYouWantToRejectTrips', { count: 1 })}
          message={
            moreInfoTripsCount
              ? isReceiptViewType
                ? t('rejectAllTripsAndSomeWithMoreInfoInReceipt')
                : t('rejectAllTripsAndSomeWithMoreInfoInReimbursement')
              : undefined
          }
          onOk={handleTripDeny}
          onCancel={closeTripRejectionConfirmation}
          okText={t('yes')}
          cancelText={t('no')}
        />
      )}

      {receipt && hasCompanyManagerOrAdminRole(authUserProfile) && (
        <ReimbursementReceiptConfirmationWithMoreInfoTripModal
          t={t}
          type="reimbursement"
          visible={isApprovalWithMoreInfoVisible}
          tripsCount={moreInfoTripsCount}
          onCancel={closeApprovalConfirmation}
          isApprovingReceipt={isApprovingReceipt}
          onApproveAllTripsClick={() => handleApprove(match.params.id, 'approveAll')}
          onTripsAwaitingForApprovalClick={() => handleApprove(match.params.id, 'approvePending')}
        />
      )}

      {receipt && (
        <SubmitReimbursementsForApprovalConfirmation
          t={t}
          count={1}
          visible={isResubmitConfirmationVisible}
          isSubmitting={isResubmittingReceiptsForApproval}
          onCancel={closeResubmitConfirmation}
          onConfirm={() => handleResubmitForApproval(receipt?._id)}
        />
      )}

      <TripsCommentsModal
        t={t}
        visible={isTripCommentsModalVisible}
        onCancel={closeTripCommentsModal}
        loading={tripCommentsQuery.isFetching}
        comments={tripCommentsQuery.data.documents}
        paginationConfig={commentsPaginationConfig}
        onPageChange={handleCommentsPageChange}
        isAddingComment={isAddingComment}
        onAddComment={addCommentToTrip}
        canAddComment={canAddCommentOnTrip(
          tripIdToViewComments ? find(receiptTrips, { _id: tripIdToViewComments }) : undefined,
        )}
      />

      {receipt &&
        hasCompanyManagerOrAdminRole(authUserProfile) &&
        canUpdateReceiptStatus(authUserProfile, undefined, true) && (
          <ReceiptFooterAdminActionBoxContent
            // General
            t={t}
            isLoading={!receipt || isReceiptLoading || isRejectingReceipt || isApprovingReceipt}
            loggedInUser={authUserProfile}
            currentCompany={currentCompany}
            companyGroups={groupList}
            // Receipt
            receipt={receipt}
            onApprove={openApprovalConfirmation}
            onDeny={openRejectionConfirmation}
            onResubmitForApprovalFixed={openResubmitConfirmation}
            // More info
            isRequesting={isProcessingMoreInfoRequest}
            isRequestingMoreInfo={isRequestingMoreInfo}
            receiptTrips={receiptTrips}
            selectedTrips={selectedTripsMoreInfo}
            disableRequestMoreInfo={moreInfoTripsCount === totalTripsCount}
            toggleRequestMoreInfo={toggleRequestMoreInfo}
            onSendToEmployee={handleMoreInfoRequest}
          />
        )}

      {receipt && hasUserRole(authUserProfile) && (
        <ReceiptFooterUserActionBoxContent
          // General
          t={t}
          isLoading={isReceiptLoading}
          loggedInUser={authUserProfile}
          // Receipt
          receipt={receipt}
          onResubmitForApprovalFixed={openResubmitConfirmation}
        />
      )}

      {hasCompanyAdminRole(authUserProfile) && (
        <ReceiptDeleteConfirmationModal
          t={t}
          isReimbursement
          visible={isDeleteReceiptModalVisible}
          loading={deleteReceiptMutation.isLoading}
          onOk={deleteReceiptMutation.mutateAsync}
          onCancel={closeDeleteReceiptModal}
        />
      )}

      {receipt?.type === RECEIPT_TYPES.VARIABLE_TYPE && (
        <TripsSubmissionHistoryModal
          t={t}
          rbId={receipt?._id}
          visible={isSubmissionHistoryVisible}
          submissionHistoryList={receipt.tripsHistory}
          onCancel={toggleSubmissionHistoryModal}
        />
      )}

      {companySettings?.settings?.mileageCap && (
        <Drawer
          width={setModalFullscreenWidth(848)}
          placement="right"
          open={isMonthlyChartVisible}
          onClose={() => setIsMonthlyChartVisible(false)}
        >
          <MetabaseMonthlyMileageChart
            userId={get(receipt, 'submittedBy._id')}
            companyId={currentCompany._id}
            t={t}
          />
        </Drawer>
      )}
    </div>
  );
};

export default withNamespaces()(ReimbursementsDetailsOnPeriodView);
